import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { CommonService } from 'src/app/shared/common.service';
import { QuotesService } from '../services/quotes.services';
import * as quotationsActions from './quotes.actions';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { IAskForQuotation, IQuotation, IQuotationDetail } from '../models/quotes.models';
import { AlertEnums } from 'src/app/shared/enums/shared.enums';
import { SetLoading } from 'src/app/shared/store/shared.actions';
import { GetQuotations } from './quotes.actions';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class QuotesEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private quotesService: QuotesService,
    private commonService: CommonService,
    private translateService: TranslateService
  ) {}

  // fetch All Quotations
  FetchAllQuotationsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.GetQuotations),
      switchMap(() =>
        this.quotesService.getQuotations().pipe(
          map((res: APIResponse<IQuotation>) =>
            quotationsActions.GetQuotationsSuccess({ quotations: res.data })
          ),
          tap(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
          }),
          catchError(() =>
            of(quotationsActions.GetQuotationsError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // get Quotation By Id
  GetQuotationByIdEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.GetQuotationById),
      switchMap((action) =>
        this.quotesService.getQuotationById(action.id).pipe(
          map((res: APIResponse<IQuotationDetail>) =>
            quotationsActions.GetQuotationByIdSuccess({ quotation: res.object! })
          ),
          tap(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
          }),
          catchError(() =>
            of(quotationsActions.GetQuotationByIdError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // add AskForQuotation
  AddAskForQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.AddAskForQuotation),
      switchMap((action) =>
        this.quotesService.addAskForQuotation(action.ask_for_quotation).pipe(
          map((res: APIResponse<IQuotation>) =>
            quotationsActions.AddAskForQuotationSuccess({ ask_for_quotation: res.object! })
          ),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`toast_messages.${AlertEnums.QUOTATION_SENT_SUCCESS}`),
              'SUCCESS'
            );
            this.store.dispatch(GetQuotations());
          }),
          catchError(() =>
            of(quotationsActions.AddAskForQuotationError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
                this.commonService.showToast(
                  this.translateService.instant(`toast_messages.${AlertEnums.QUOTATION_ERROR}`),
                  'ERROR'
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // draft AskForQuotation
  DraftAskForQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.DraftAskForQuotation),
      switchMap((action) =>
        this.quotesService.draftAskForQuotation(action.ask_for_quotation).pipe(
          map((res: APIResponse<IQuotation>) =>
            quotationsActions.DraftAskForQuotationSuccess({ ask_for_quotation: res.object! })
          ),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`toast_messages.${AlertEnums.QUOTATION_DRAFT_SUCCESS}`),
              'SUCCESS'
            );
            this.store.dispatch(GetQuotations());
          }),
          catchError(() =>
            of(quotationsActions.DraftAskForQuotationError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
                this.commonService.showToast(
                  this.translateService.instant(`toast_messages.${AlertEnums.QUOTATION_ERROR}`),
                  'ERROR'
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // edit and AskForQuotation
  EditAndAskForQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.EditAndAskForQuotation),
      switchMap((action) =>
        this.quotesService.editAndAskForQuotation(action.id, action.ask_for_quotation).pipe(
          map((res: APIResponse<IQuotation>) =>
            quotationsActions.EditAndAskForQuotationSuccess({ ask_for_quotation: res.object! })
          ),
          tap(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
            this.commonService.showToast(
              this.translateService.instant(`toast_messages.${AlertEnums.QUOTATION_EDIT_SUCCESS}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(quotationsActions.EditAndAskForQuotationError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
                this.commonService.showToast(
                  this.translateService.instant(`toast_messages.${AlertEnums.QUOTATION_ERROR}`),
                  'ERROR'
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // send request
  SendRequestEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.SendRequest),
      switchMap((action) =>
        this.quotesService.sendRequest(action.id).pipe(
          map((res: APIResponse<IQuotation>) =>
            quotationsActions.SendRequestSuccess({ quotation: res.object! })
          ),
          tap(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
            this.commonService.showToast(
              this.translateService.instant(
                `toast_messages.${AlertEnums.QUOTATION_REQUEST_SUCCESS}`
              ),
              'SUCCESS'
            );
            this.store.dispatch(GetQuotations());
          }),
          catchError(() =>
            of(quotationsActions.SendRequestError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
                this.commonService.showToast(
                  this.translateService.instant(`toast_messages.${AlertEnums.QUOTATION_ERROR}`),
                  'ERROR'
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // accept Quotation
  AcceptQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.AcceptQuotation),
      switchMap((action) =>
        this.quotesService.acceptQuotation(action.id).pipe(
          map((res: APIResponse<IQuotation>) =>
            quotationsActions.AcceptQuotationSuccess({ quotation: res.object! })
          ),
          tap(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
            this.commonService.showToast(
              this.translateService.instant(
                `toast_messages.${AlertEnums.QUOTATION_ACCEPT_SUCCESS}`
              ),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(quotationsActions.AcceptQuotationError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
                this.commonService.showToast(
                  this.translateService.instant(`toast_messages.${AlertEnums.QUOTATION_ERROR}`),
                  'ERROR'
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // decline Quotation
  DeclineQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.DeclineQuotation),
      switchMap((action) =>
        this.quotesService.declineQuotation(action.id).pipe(
          map((res: APIResponse<IQuotation>) =>
            quotationsActions.DeclineQuotationSuccess({ quotation: res.object! })
          ),
          tap(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
            this.commonService.showToast(
              this.translateService.instant(
                `toast_messages.${AlertEnums.QUOTATION_CANCEL_SUCCESS}`
              ),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(quotationsActions.DeclineQuotationError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
                this.commonService.showToast(
                  this.translateService.instant(`toast_messages.${AlertEnums.QUOTATION_ERROR}`),
                  'ERROR'
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );
}
